import type {
  Declaration,
  DeclarationStatus,
  DeclarationTransition,
  Pedigree,
} from '@pedigreesmart/core'

import type { ApiClient } from './client.js'
import type {
  AnimalRead,
  AnimalWrite,
  BreedRead,
  DocumentRead,
  EventRead,
  HealthRecordRead,
  LitterRead,
  LookupRead,
  MembershipRead,
  MembershipWrite,
  MembershipYearRead,
  OwnershipRead,
  PersonRead,
  PersonWrite,
  TenantUserRead,
} from './generated.js'

export interface Page<T> {
  member: readonly T[]
  /**
   * `totalItems` in camelCase, e non è una svista.
   *
   * È una delle tre eccezioni allo snake_case dell'API (docs/08 § 1): viene
   * da Hydra, non da noi, e il convertitore di nomi non la tocca. Qui era
   * dichiarata `total_items`: TypeScript si fidava e il conteggio sarebbe
   * arrivato `undefined` a runtime, senza un solo errore di compilazione.
   */
  totalItems: number
}

export interface AnimalQuery {
  /**
   * `species.code` e non `species`.
   *
   * Il filtro è dichiarato sulla proprietà annidata (`ApiFilter` su
   * `Animal`), quindi `?species=canine` **non filtra**: API Platform ignora i
   * parametri che non riconosce, in silenzio. Chi sceglieva «Cavalli» nel
   * selettore riceveva tutti i soggetti di tutte le specie senza che niente
   * segnalasse un errore.
   */
  'species.code'?: string
  'breed.id'?: number
  'breeder.id'?: number
  /** I nati di una cucciolata: è l'unico modo di risalirci, non c'è una rotta. */
  'litter.id'?: number
  sex?: 'M' | 'F' | 'U'
  q?: string
  page?: number
  items_per_page?: number
}

/**
 * Thin resource wrappers over the client.
 *
 * These are hand-written for now; once `make openapi` runs, the request and
 * response shapes come from the generated types instead, so a backend change
 * that breaks the contract fails at compile time rather than at runtime.
 */
export class Animals {
  constructor(private readonly api: ApiClient) {}

  list(query: AnimalQuery = {}, signal?: AbortSignal): Promise<Page<AnimalRead>> {
    return this.api.get<Page<AnimalRead>>('/animals', query as Record<string, string | number>, signal)
  }

  get(id: number, signal?: AbortSignal): Promise<AnimalRead> {
    return this.api.get<AnimalRead>(`/animals/${id}`, undefined, signal)
  }

  pedigree(id: number, generations?: number, signal?: AbortSignal): Promise<Pedigree> {
    return this.api.get<Pedigree>(
      `/animals/${id}/pedigree`,
      generations ? { generations } : undefined,
      signal,
    )
  }

  create(body: Partial<AnimalWrite>): Promise<AnimalRead> {
    return this.api.post<AnimalRead>('/animals', body)
  }

  /**
   * Il certificato genealogico, in PDF.
   *
   * Risponde `404` quando il soggetto non è iscritto al registro: non è un
   * guasto ma la risposta giusta, e va detta come tale a chi ha premuto il
   * pulsante.
   */
  certificate(id: number, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
    return this.api.file(`/animals/${id}/certificate`, signal)
  }

  update(id: number, body: Partial<AnimalWrite>): Promise<AnimalRead> {
    return this.api.patch<AnimalRead>(`/animals/${id}`, body)
  }

  /**
   * Mette o toglie il soggetto dalla vetrina pubblica.
   *
   * Non passa da `update()` e non è una scelta di stile: `for_sale` non è più
   * un campo scrivibile della scheda, perché pubblicare all'esterno vuole
   * `listing.manage` e correggere un dato no. Un soggetto mai iscritto,
   * radiato o deceduto risponde `409`.
   */
  listing(id: number, inVendita: boolean): Promise<AnimalListing> {
    return this.api.put<AnimalListing>(`/animals/${id}/listing`, { for_sale: inVendita })
  }
}

export interface AnimalListing {
  id: number
  for_sale: boolean
  /** Se comparirà davvero: l'interruttore da solo non basta. */
  publicly_listable: boolean
}

/**
 * Il consumo su cui l'associazione viene fatturata.
 *
 * Questa interfaccia è stata riscritta il 9 agosto. Prima dichiarava
 * `base_fee_cents`, `per_animal_cents` e `estimated_total_cents` — un importo
 * stimato — e puntava a una rotta **che non esisteva**: `GET /billing/usage`
 * non era mai stato costruito, e il metodo avrebbe risposto `404`.
 *
 * Gli importi non ci sono più, e non per pigrizia: il listino lo tiene l'altro
 * team e la loro fatturazione guarda il massimo raggiunto nel periodo, non il
 * valore di oggi (docs/07 § 5). Un totale calcolato da noi sarebbe una cifra
 * plausibile e sbagliata, cioè peggio di nessuna cifra — verrebbe creduta.
 */
export interface BillingUsage {
  /** La data a cui il conteggio si riferisce (`AAAA-MM-GG`). */
  on: string
  /** I soggetti attivi, con la definizione di docs/07 § 1. */
  active: number
  /** Lo stesso totale, ripartito per specie: serve a poterlo verificare. */
  by_species: Record<string, number>
  computed_at: string
}

export class Billing {
  constructor(private readonly api: ApiClient) {}

  usage(on?: string, signal?: AbortSignal): Promise<BillingUsage> {
    return this.api.get<BillingUsage>('/billing/usage', on ? { on } : undefined, signal)
  }
}

export interface DeclarationQuery {
  status?: DeclarationStatus
  type?: string
  page?: number
  items_per_page?: number
}

export class Declarations {
  constructor(private readonly api: ApiClient) {}

  list(query: DeclarationQuery = {}, signal?: AbortSignal): Promise<Page<Declaration>> {
    return this.api.get<Page<Declaration>>(
      '/declarations',
      query as Record<string, string | number>,
      signal,
    )
  }

  get(id: number, signal?: AbortSignal): Promise<Declaration> {
    return this.api.get<Declaration>(`/declarations/${id}`, undefined, signal)
  }

  /**
   * Apre una denuncia, sempre in bozza.
   *
   * Il contenuto (`payload`) cambia col tipo: sono le chiavi che il server
   * legge davvero al momento della registrazione — `name`/`sex`/`owner_id`
   * per un'iscrizione, `sire_id`/`mating_date` per una monta, `puppies[]` per
   * una nascita, `new_owner_id`/`date` per un passaggio. Non è uno schema
   * libero: quello che non viene letto resta scritto nel documento senza
   * avere alcun effetto.
   */
  create(body: Record<string, unknown>): Promise<Declaration> {
    return this.api.post<Declaration>('/declarations', body)
  }

  /**
   * Applica una transizione.
   *
   * `reason` serve solo a `reject` ed è obbligatorio lì: una denuncia respinta
   * senza motivo costringe chi l'ha presentata a indovinare cosa correggere.
   *
   * Gli esiti che il chiamante deve saper distinguere sono due, e nessuno dei
   * due è un guasto: `422` con `violations` quando i dati non bastano per
   * quella transizione, `409` quando la transizione non è ammessa dallo stato
   * attuale — per esempio perché qualcun altro l'ha già fatta avanzare.
   */
  transition(
    id: number,
    transition: DeclarationTransition,
    reason?: string,
  ): Promise<Declaration> {
    return this.api.post<Declaration>(
      `/declarations/${id}/${transition}`,
      reason === undefined ? {} : { reason },
    )
  }

  /**
   * Cosa succederebbe applicando `register`, senza applicarlo.
   *
   * È l'unica transizione che tocca il registro, e l'anteprima esiste perché
   * si possa guardare prima invece di scoprirlo dopo.
   */
  preview(id: number, signal?: AbortSignal): Promise<{
    ready: boolean
    problems: readonly string[]
    effects: readonly Record<string, unknown>[]
  }> {
    return this.api.get(`/declarations/${id}/preview`, undefined, signal)
  }
}

/**
 * I filtri di `/people`, e solo quelli.
 *
 * **Non c'è `q`**: a differenza dei soggetti, le persone non hanno una ricerca
 * libera. Metterla qui darebbe una casella che sembra funzionare e restituisce
 * l'elenco intero, perché API Platform ignora in silenzio i parametri che non
 * riconosce. La ricerca dell'interfaccia usa quindi `last_name`, che è
 * parziale e non distingue maiuscole (`bian` trova `Bianchi`).
 *
 * Se servisse cercare per nome *o* cognome insieme, va aggiunto un filtro sul
 * backend: non si può comporre dal client.
 */
export interface PersonQuery {
  last_name?: string
  first_name?: string
  organization_name?: string
  kennel_prefix?: string
  tax_code?: string
  city?: string
  breeder?: boolean
  'order[last_name]'?: 'asc' | 'desc'
  page?: number
  items_per_page?: number
}

export class People {
  constructor(private readonly api: ApiClient) {}

  list(query: PersonQuery = {}, signal?: AbortSignal): Promise<Page<PersonRead>> {
    return this.api.get<Page<PersonRead>>(
      '/people',
      query as Record<string, string | number>,
      signal,
    )
  }

  get(id: number, signal?: AbortSignal): Promise<PersonRead> {
    return this.api.get<PersonRead>(`/people/${id}`, undefined, signal)
  }

  create(body: PersonWrite): Promise<PersonRead> {
    return this.api.post<PersonRead>('/people', body)
  }

  update(id: number, body: Partial<PersonWrite>): Promise<PersonRead> {
    return this.api.patch<PersonRead>(`/people/${id}`, body)
  }

  /**
   * Tutto quello che l'associazione tiene sulla persona (art. 15 GDPR).
   *
   * Non è un doppione di `get`: comprende il tesseramento, i soggetti e i
   * documenti, cioè i dati che stanno altrove ma la riguardano.
   */
  export(id: number, signal?: AbortSignal): Promise<Record<string, unknown>> {
    return this.api.get<Record<string, unknown>>(`/people/${id}/export`, undefined, signal)
  }

  /**
   * Cancellazione (art. 17): i contatti spariscono, il nome resta.
   *
   * Il nome è stampato su certificati già consegnati ad altre persone, quindi
   * toglierlo falsificherebbe documenti altrui. Non è ripetibile: la seconda
   * chiamata risponde `409`.
   */
  anonymize(id: number): Promise<PersonRead> {
    return this.api.post<PersonRead>(`/people/${id}/anonymize`, {})
  }
}

export interface MembershipQuery {
  'person.id'?: number
  /** Parziale e insensibile alle maiuscole, come su `/people`. */
  'person.lastName'?: string
  member_number?: string
  page?: number
  items_per_page?: number
}

export class Memberships {
  constructor(private readonly api: ApiClient) {}

  list(query: MembershipQuery = {}, signal?: AbortSignal): Promise<Page<MembershipRead>> {
    return this.api.get<Page<MembershipRead>>(
      '/memberships',
      query as Record<string, string | number>,
      signal,
    )
  }

  get(id: number, signal?: AbortSignal): Promise<MembershipRead> {
    return this.api.get<MembershipRead>(`/memberships/${id}`, undefined, signal)
  }

  /**
   * Apre un tesseramento.
   *
   * `joined_on` può mancare: il server la mette a oggi. Fino al 9 agosto
   * mancava anche `member_number` e la risposta era un `400` che nominava la
   * classe PHP e il parametro del costruttore; ora è un `422` sul campo.
   */
  create(body: MembershipWrite): Promise<MembershipRead> {
    return this.api.post<MembershipRead>('/memberships', body)
  }

  /**
   * Aggiunge un anno.
   *
   * `membership` è un IRI, non un id: è la forma che API Platform si aspetta
   * per le relazioni, e un numero produce un `400` poco chiaro.
   */
  addYear(membershipId: number, year: number, fee?: number): Promise<MembershipYearRead> {
    return this.api.post<MembershipYearRead>('/membership-years', {
      membership: `/api/v1/memberships/${membershipId}`,
      year,
      ...(fee === undefined ? {} : { fee }),
    })
  }
}

/**
 * Le razze di una specie.
 *
 * Il filtro è `species.code`, come per i soggetti: `?species=canine` verrebbe
 * ignorato in silenzio e tornerebbero le razze di tutte le specie — un elenco
 * in cui un'associazione cinofila troverebbe i bovini.
 */
export class Breeds {
  constructor(private readonly api: ApiClient) {}

  list(speciesCode: string, signal?: AbortSignal): Promise<Page<BreedRead>> {
    return this.api.get<Page<BreedRead>>(
      '/breeds',
      // Un elenco solo, senza paginazione a schermo: le razze di una specie
      // sono decine, non migliaia. Se un giorno superassero questo tetto, chi
      // usa il modulo lo vedrebbe — il conteggio è confrontato con la
      // lunghezza dell'elenco e la differenza viene detta.
      //
      // Niente `order[name]`: su `Breed` non c'è un OrderFilter, quindi il
      // parametro verrebbe ignorato in silenzio e sembrerebbe che l'ordine
      // venga da lì. Arriva invece dall'ordinamento predefinito della risorsa
      // (`order: ['name' => 'ASC']`), che vale per tutti i client.
      { 'species.code': speciesCode, items_per_page: 400 },
      signal,
    )
  }
}

/**
 * Le intestazioni di un soggetto: chi ne è stato proprietario, e quando.
 *
 * Il filtro è `animal.id`. Passarlo **vuoto** non filtra: API Platform ignora
 * i parametri senza valore e risponde con l'elenco intero — è successo in una
 * prova, e sembrava un filtro rotto.
 */
export class Ownerships {
  constructor(private readonly api: ApiClient) {}

  byAnimal(animalId: number, signal?: AbortSignal): Promise<Page<OwnershipRead>> {
    return this.api.get<Page<OwnershipRead>>(
      '/animal-ownerships',
      // Niente `order[...]`: l'ordine dal più recente è già quello predefinito
      // della risorsa (`order: ['startedOn' => 'DESC']`), e un parametro senza
      // un filtro dietro verrebbe ignorato in silenzio.
      { 'animal.id': animalId, items_per_page: 50 },
      signal,
    )
  }
}

/**
 * I profili che possono entrare per conto dell'associazione.
 *
 * Non si creano da qui: un profilo nuovo nasce da un invito o dal comando
 * `pedigreesmart:user:create`, perché creare credenziali richiede di scegliere
 * una password e di consegnarla, e non è una cosa che si fa con un `POST` da
 * un elenco. Da qui si cambia il ruolo e si toglie l'accesso.
 */
export class TenantUsers {
  constructor(private readonly api: ApiClient) {}

  list(signal?: AbortSignal): Promise<Page<TenantUserRead>> {
    return this.api.get<Page<TenantUserRead>>('/tenant-users', { items_per_page: 100 }, signal)
  }

  update(id: number, body: { role?: string; active?: boolean }): Promise<TenantUserRead> {
    return this.api.patch<TenantUserRead>(`/tenant-users/${id}`, body)
  }
}

/**
 * Referti sanitari ed eventi di un soggetto.
 *
 * Stanno insieme perché rispondono alla stessa domanda — che cosa si sa di
 * questo soggetto oltre alla sua genealogia — e perché si filtrano allo stesso
 * modo, su `animal.id`.
 */
export class AnimalRecords {
  constructor(private readonly api: ApiClient) {}

  health(animalId: number, signal?: AbortSignal): Promise<Page<HealthRecordRead>> {
    return this.api.get<Page<HealthRecordRead>>(
      '/health-records',
      { 'animal.id': animalId, items_per_page: 50 },
      signal,
    )
  }

  events(animalId: number, signal?: AbortSignal): Promise<Page<EventRead>> {
    return this.api.get<Page<EventRead>>(
      '/events',
      { 'animal.id': animalId, items_per_page: 50 },
      signal,
    )
  }

  /**
   * Registra un referto.
   *
   * `category` è obbligatoria e chiusa: un valore fuori elenco risponde `422`
   * con «Il valore selezionato non è una scelta valida», che non dice quali
   * siano i valori ammessi — per questo l'interfaccia li offre in una tendina
   * invece di lasciarli digitare.
   */
  addHealth(body: {
    animal: string
    category: string
    description?: string
    performed_on?: string
    result?: string
    provider?: string
    notes?: string
  }): Promise<HealthRecordRead> {
    return this.api.post<HealthRecordRead>('/health-records', body)
  }
}

/**
 * I documenti allegati a qualcosa.
 *
 * Il filtro è su `ownerType` **e** `ownerId`: sono due colonne e non una
 * relazione, perché un documento può appartenere a un soggetto, a una persona,
 * a una denuncia o a una nidiata — quattro tabelle diverse.
 */
export class Documents {
  constructor(private readonly api: ApiClient) {}

  of(ownerType: string, ownerId: number, signal?: AbortSignal): Promise<Page<DocumentRead>> {
    return this.api.get<Page<DocumentRead>>(
      '/documents',
      // `owner_type` e non `ownerType`: filtrano entrambi — API Platform
      // accetta anche il nome della proprietà PHP — ma solo il primo è
      // dichiarato nello schema, ed è quello che leggerà chiunque generi un
      // client dal contratto.
      { owner_type: ownerType, owner_id: ownerId, items_per_page: 50 },
      signal,
    )
  }

  /**
   * Carica un file.
   *
   * Il tipo lo decide il contenuto, non il nome: un file rifiutato torna come
   * `422` sul campo `file`, con scritto perché.
   */
  upload(input: {
    file: File
    ownerType: string
    ownerId: number
    title?: string
  }): Promise<DocumentRead> {
    const corpo = new FormData()

    corpo.append('file', input.file)
    corpo.append('owner_type', input.ownerType)
    corpo.append('owner_id', String(input.ownerId))
    if (input.title) corpo.append('title', input.title)

    return this.api.form<DocumentRead>('/documents', corpo)
  }

  /** L'indirizzo da cui si scarica: richiede il token, quindi passa da `file()`. */
  download(id: number): Promise<{ blob: Blob; filename: string }> {
    return this.api.file(`/documents/${id}/download`)
  }

  /**
   * Elimina un documento.
   *
   * La risposta è `204` senza corpo, quindi non c'è niente da restituire —
   * `undefined` e non `void`, che come argomento di tipo non è ammesso.
   */
  remove(id: number): Promise<undefined> {
    return this.api.delete<undefined>(`/documents/${id}`)
  }
}

/**
 * Le liste configurabili dell'associazione.
 *
 * Un valore di sistema (senza associazione) si vede ma non si tocca: la
 * modifica e la cancellazione rispondono `403`. Il client non prova a
 * indovinare quali siano — la risposta non porta il tenant — quindi
 * l'interfaccia lascia provare e riporta il rifiuto, invece di nascondere
 * righe in base a una supposizione.
 */
export class Lookups {
  constructor(private readonly api: ApiClient) {}

  list(kind: string, signal?: AbortSignal): Promise<Page<LookupRead>> {
    return this.api.get<Page<LookupRead>>('/lookups', { kind, items_per_page: 100 }, signal)
  }

  create(body: { kind: string; label: string; code?: string; position?: number }): Promise<LookupRead> {
    return this.api.post<LookupRead>('/lookups', body)
  }

  update(id: number, body: { label?: string; active?: boolean; position?: number }): Promise<LookupRead> {
    return this.api.patch<LookupRead>(`/lookups/${id}`, body)
  }
}

/**
 * I dati dell'associazione, letti e corretti da lei stessa.
 *
 * Non è una risorsa di API Platform, quindi il tipo è scritto qui: sono i
 * quindici campi che il controller espone, e l'elenco dei modificabili è
 * chiuso anche lì — mandarne un altro fa fallire la richiesta invece di
 * essere ignorato.
 */
export interface TenantSettings {
  /** Nell'indirizzo pubblico della vetrina: si legge, non si scrive. */
  slug: string
  /** Lo decide chi fattura: si legge, non si scrive. */
  status: string
  name: string
  legal_name: string | null
  vat_number: string | null
  tax_code: string | null
  authorizations: string | null
  address: string | null
  postal_code: string | null
  city: string | null
  province: string | null
  country: string
  phone: string | null
  email: string | null
  website: string | null
}

export class TenantSettingsResource {
  constructor(private readonly api: ApiClient) {}

  read(signal?: AbortSignal): Promise<TenantSettings> {
    return this.api.get<TenantSettings>('/tenant/settings', undefined, signal)
  }

  /**
   * Salva i campi indicati.
   *
   * Passa da `patch()` come tutto il resto, quindi manda
   * `application/merge-patch+json`. Qui non serve — è un controller e legge il
   * corpo grezzo, verificato: accetta anche `application/json` — ma un client
   * con due comportamenti per lo stesso metodo è un client in cui bisogna
   * ricordarsi quale sia il caso speciale.
   */
  save(body: Partial<Omit<TenantSettings, 'slug' | 'status'>>): Promise<TenantSettings> {
    return this.api.patch<TenantSettings>('/tenant/settings', body)
  }
}

/**
 * Una voce del registro degli accessi.
 *
 * Non è una risorsa di API Platform — il registro è di sola lettura e non ha
 * uno schema generato — quindi il tipo sta qui, sui campi che il controller
 * scrive davvero.
 */
export interface AuditEntry {
  id: number
  action: string
  entity_type: string | null
  entity_id: number | null
  changes: Record<string, unknown>
  user: { id: number; name: string; email: string } | null
  ip_address: string | null
  created_at: string
}

export interface AuditQuery {
  action?: string
  entity_type?: string
  entity_id?: number
  user_id?: number
  /** Solo `AAAA-MM-GG`: qualunque altra forma è rifiutata con 422. */
  from?: string
  to?: string
  page?: number
  per_page?: number
}

export interface AuditPage {
  items: AuditEntry[]
  total: number
  page: number
  per_page: number
  /** Le azioni presenti davvero, per riempire il filtro senza inventarle. */
  actions: string[]
}

export class Audit {
  constructor(private readonly api: ApiClient) {}

  list(query: AuditQuery = {}, signal?: AbortSignal): Promise<AuditPage> {
    return this.api.get<AuditPage>('/audit', query as Record<string, string | number>, signal)
  }
}

export interface LitterQuery {
  'species.code'?: string
  'dam.id'?: number
  'sire.id'?: number
  'breeder.id'?: number
  page?: number
  items_per_page?: number
}

export class Litters {
  constructor(private readonly api: ApiClient) {}

  list(query: LitterQuery = {}, signal?: AbortSignal): Promise<Page<LitterRead>> {
    return this.api.get<Page<LitterRead>>(
      '/litters',
      // L'ordine dalla più recente è il predefinito della risorsa
      // (`order: ['birthDate' => 'DESC']`): non si passa un `order[...]`, che
      // senza un OrderFilter dietro verrebbe ignorato in silenzio.
      query as Record<string, string | number>,
      signal,
    )
  }
}

export function createResources(api: ApiClient) {
  return {
    animals: new Animals(api),
    declarations: new Declarations(api),
    billing: new Billing(api),
    people: new People(api),
    memberships: new Memberships(api),
    breeds: new Breeds(api),
    ownerships: new Ownerships(api),
    tenantUsers: new TenantUsers(api),
    records: new AnimalRecords(api),
    documents: new Documents(api),
    lookups: new Lookups(api),
    tenantSettings: new TenantSettingsResource(api),
    litters: new Litters(api),
    audit: new Audit(api),
  }
}
